// object-handling-layers.ts — a creature's telekinetic object-handling grants (2026-10-07; Geno's "Psion Subclass Revisions And
// Changes v12", Mind of the Telekinetic L10 "Mental Grip" and L35 "Gravity Sovereign"). Two generic layer types:
//
//   object_interaction_range_ft  (int)            — the holder's reach in feet for UNATTENDED objects: a loose row on the encounter
//                                                    floor (pick up) and a container standing on the floor (take from / put into).
//                                                    Read by shared/inventory-proximity.ts; never an attack / give / pickpocket reach.
//   throw_object_max_size_type   (size_type id)   — the largest Size Type the holder can throw. An object above the thrower's own size
//                                                    but within this cap is hurled by the mind: the "two sizes larger cannot be thrown"
//                                                    ceiling and the hand rule stand down (throw-object-gate.ts). The lift (weight vs
//                                                    Max Carrying Capacity) still applies.
//
// Both are carried by passives in practice, so the read walks the holder's own layers plus every ACTIVE passive in its passive bucket
// (gear-surfaced passives included — the compile puts them there), every held feat and each passive a feat grants. The HIGHEST grant
// wins; grants never stack (two 60 ft sources are 60 ft). Browser-only rule (front-end rule, CLAUDE.md): no PHP twin — the pickup reach
// gate is the browser's (move_draggable_inventory_inc.php "Reach is checked by the browser") and PHP's throw gate is retired (R-028).
// Pure: every read goes through the injected readers.
import { THROW_OBJECT_SIZE_ORDER, THROW_OBJECT_ABILITY_ID } from '../../shared/throw-object-range';
import { abilityBucket, passiveEntryActive } from './spell-components';

type Row = Record<string, unknown>;

export const OBJECT_INTERACTION_RANGE_FT = 'object_interaction_range_ft';
export const THROW_OBJECT_MAX_SIZE_TYPE = 'throw_object_max_size_type';

export interface ObjectHandlingReaders {
    /** layer_value() */
    value: (row: Row | null, key: string) => unknown;
    /** layer_entries() */
    entries: (row: Row | null, key: string) => unknown[];
    /** A bucket / layer-list entry (or a bare id) → its FULL definition (items + item_layer_types); null when the page lacks it. */
    definition: (entry: unknown) => Row | null;
}

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? scalar(value[0]) : value;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/** A layer value's UUID: a bare id, or a shell / full-nest row's items.item_id (else item_id). */
function uuidOf(value: unknown): string {
    const v = scalar(value);
    const row = record(v);
    const id = String((row ? (record(row.items)?.item_id ?? row.item_id) : v) ?? '').trim().toUpperCase();
    return UUID.test(id) ? id : '';
}
/** A bucket / layer-list entry's own id (layer_list_entry_uuid's head, else ability_id). */
function entryUuid(entry: unknown): string {
    if (typeof entry === 'string') return (entry.split('|')[0] ?? '').trim().toUpperCase();
    const row = record(entry);
    return String(row?.item_layer_effect ?? record(row?.items)?.item_id ?? row?.item_id ?? row?.ability_id ?? '').split('|')[0]!.trim().toUpperCase();
}
function numberOf(value: unknown): number {
    const n = Number(scalar(value));
    return Number.isFinite(n) ? n : 0;
}
/** The definition an entry stands for: its inline full row (a {source, item_arr} wrapper, or an entry that is itself a full row), else the reader's. */
function entryDefinition(entry: unknown, read: ObjectHandlingReaders): Row | null {
    const row = record(entry);
    const nested = record(row?.item_arr);
    if (nested && record(nested.item_layer_types)) return nested;
    if (row && record(row.item_layer_types)) return row;
    return read.definition(entry);
}

/** Every row whose object-handling grants count for this creature: itself, its active passives, its feats and their granted passives. */
export function objectHandlingSources(creature: Row, read: ObjectHandlingReaders): Row[] {
    const out: Row[] = [creature];
    const seen = new Set<Row>([creature]);
    const add = (definition: Row | null): void => {
        if (!definition || seen.has(definition)) return;
        seen.add(definition);
        out.push(definition);
    };
    // A passive the compile stamped INACTIVE stays off even when a feat also grants it (Codex review 2026-10-07).
    const inactive = new Set<string>();
    for (const entry of abilityBucket(creature, 'passive_ability', read)) {
        if (passiveEntryActive(entry)) add(entryDefinition(entry, read));
        else if (record(entry)) inactive.add(entryUuid(entry));
    }
    // Feat conditions are not evaluated (casterHoldsPassive's precedent): a held feat's grant counts.
    for (const featEntry of abilityBucket(creature, 'feat', read)) {
        const feat = entryDefinition(featEntry, read);
        if (!feat) continue;
        add(feat);
        for (const granted of read.entries(feat, 'passive_ability')) {
            if (!inactive.has(entryUuid(granted))) add(entryDefinition(granted, read));
        }
    }
    return out;
}

/** object_interaction_range_ft: the highest grant in feet (0 = none — the creature's ordinary reach governs). */
export function objectInteractionRangeFt(creature: Row, read: ObjectHandlingReaders): number {
    let best = 0;
    for (const source of objectHandlingSources(creature, read)) best = Math.max(best, numberOf(read.value(source, OBJECT_INTERACTION_RANGE_FT)));
    return best;
}

// ── HURLED OBJECT (2026-10-08, Psion Telekinetic hurl mentas — Force Pebble / Debris Volley / Hurl Object / Siege Throw) ──────────────
// An ABILITY carrying hurls_unattended_object (a SizeType id: the largest object it can hurl) needs a picked unattended object: a row lying
// loose on the caster's encounter floor, not a world fixture, no larger than the cap, within the ability's own object_interaction_range_ft
// of the caster. The object flies from where it lies to the target (object-card/hurl-object.ts lands it; PHP only persists — R-188).
export const HURLS_UNATTENDED_OBJECT = 'hurls_unattended_object';
/** The projectile is never an affected entity of its own hurl cast; other inventory instances remain eligible. */
export function excludeHurlObjectTargets<T extends Row>(ability: Row, inventoryId: unknown, targets: T[],
    read: Pick<HurlObjectReaders, 'value' | 'inventoryField'>): T[] {
    const pickedId = uuidOf(inventoryId);
    if (!pickedId || (!hurlObjectRule(ability, read) && uuidOf(record(ability.items)?.item_id ?? ability.item_id) !== THROW_OBJECT_ABILITY_ID)) return targets;
    return targets.filter(target => uuidOf(read.inventoryField(target, 'inventory_id')) !== pickedId);
}
/** Twin of throw-object-gate.ts NON_THROWABLE_ITEM_TYPES (the world parts no throw lifts). */
const HURL_FIXED_TYPES = new Set(['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor', 'vehicle_land', 'vehicle_water',
    'vehicle_air', 'war_machine', 'ore_vein', 'tree', 'trap', 'digsite', 'map_marker', 'wall_run']);
const SIZE_LABEL = ['Tiny', 'Small', 'Medium', 'Large', 'Huge', 'Gargantuan', 'Colossal', 'Titanic'];

export interface HurlObjectReaders {
    value: (row: Row | null, key: string) => unknown;
    inventoryField: (row: Row | null, key: string) => unknown;
    itemField: (row: Row | null, key: string) => unknown;
    /** An inventory row's SizeType id (per-instance inventory_size, else the template size layer). */
    itemSizeId: (row: Row) => unknown;
    /** Is the row within `rangeFt` of the caster (combat distance, the larger of reach and rangeFt)? null = geometry unknown. */
    within: (caster: Row, row: Row, rangeFt: number) => boolean | null;
}

/** The ability's hurl rule: its size cap (index into THROW_OBJECT_SIZE_ORDER) and pick range; null = not a hurling ability. */
export function hurlObjectRule(ability: Row, read: Pick<HurlObjectReaders, 'value'>): { capIndex: number; capLabel: string; rangeFt: number } | null {
    const raw = read.value(ability, HURLS_UNATTENDED_OBJECT);
    if (raw === undefined || raw === null || raw === '') return null;
    const capIndex = THROW_OBJECT_SIZE_ORDER.indexOf(uuidOf(raw));
    const rangeFt = Math.max(0, numberOf(read.value(ability, OBJECT_INTERACTION_RANGE_FT)));
    return { capIndex, capLabel: capIndex >= 0 ? SIZE_LABEL[capIndex]! : 'unknown-size', rangeFt };
}

/** Why this row cannot be hurled by this ability (null = it can). */
export function hurlObjectRowRefusal(input: { caster: Row; ability: Row; row: Row | null; encounterId: string }, read: HurlObjectReaders): string | null {
    const rule = hurlObjectRule(input.ability, read);
    if (!rule) return null;
    if (rule.capIndex < 0) return 'This ability names an unknown object size.';
    const row = input.row;
    if (!row || !uuidOf(read.inventoryField(row, 'inventory_id'))) return 'That object is not loaded on this page.';
    const deleted = read.inventoryField(row, 'inventory_deleted');
    if (deleted !== undefined && deleted !== null && deleted !== '' && deleted !== 0 && deleted !== '0') return 'That object is gone.';
    if (String(scalar(read.inventoryField(row, 'possess_id')) ?? '').trim().toUpperCase() !== input.encounterId.trim().toUpperCase() || !input.encounterId) {
        return 'Only an unattended object lying loose in the scene can be hurled.';
    }
    const type = String(scalar(read.itemField(row, 'item_type')) ?? '');
    const immovable = read.value(row, 'immovable');
    if (HURL_FIXED_TYPES.has(type) || (immovable !== undefined && immovable !== null && immovable !== '' && immovable !== 0 && immovable !== '0')) {
        return 'That is part of the world — it cannot be hurled.';
    }
    const sizeIndex = Math.max(0, THROW_OBJECT_SIZE_ORDER.indexOf(uuidOf(read.itemSizeId(row))));
    if (sizeIndex > rule.capIndex) return `That object is too big — this hurls ${rule.capLabel} or smaller objects.`;
    const within = read.within(input.caster, row, rule.rangeFt);
    if (within === null) return 'Could not determine reach. Refresh the encounter and try again.';
    if (!within) return `That object is not within ${rule.rangeFt} ft of you.`;
    return null;
}

/** throw_object_max_size_type: the largest granted SizeType id ('' = none — the ordinary size rule governs). */
export function throwObjectMaxSizeId(creature: Row, read: ObjectHandlingReaders): string {
    let best = -1;
    for (const source of objectHandlingSources(creature, read)) {
        best = Math.max(best, THROW_OBJECT_SIZE_ORDER.indexOf(uuidOf(read.value(source, THROW_OBJECT_MAX_SIZE_TYPE))));
    }
    return best >= 0 ? THROW_OBJECT_SIZE_ORDER[best]! : '';
}
